<script>
/**
 * CropBox component
 */

import { fabric } from "@vfdp-customs/customs-fabric";

export default {
  name: "UgcCropBox",
  props: {
    /**
     * Sets whether the cropbox aspect ratio is locked or not
     */
    lockSquare: {
      type: Boolean,
      default: false
    },
    /**
     * fabricjs canvas object
     */
    fabricCanvas: {
      type: Object
    },
    /**
     * Stored cropper data
     */
    tempCropData: {
      type: Object
    },
    /**
     * Min width the user can resize the cropbox
     */
    minWidth: {
      type: Number,
      default: 100

    },
    /**
     * Min height the user can resize the cropbox
     */
    minHeight: {
      type: Number,
      default: 100
    },
    /**
     * The format to export the cropped image
     * `jpeg, png`
     */
    imageFormat: {
      type: String,
      default: "jpeg",
      validator(value) {
        return ["jpeg", "jpg", "png"].includes(value);
      }
    },
    /**
     * Function that gets executed before showing the cropbox.
     */
    beforeShow: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      cropBox: null,
      startX: null,
      startY: null,
      overlayImage: null
    };
  },
  computed: {
    isOpen() {
      return this.cropBox !== null;
    },
    configuration() {
      return this.$store.state.utils.configuration;
    },
    minPlacementSize() {
      const { activeUGCComponent,  } = this.$store.state.ugc.tempData.features[this.$store.state.ugc.tempData.activeUGCIndex];
      const {pdmModel} = this.$store.state.utils;
      const minPlacementSize = this.$store.state.utils.configuration.ugc.minPlacementSize;
      const minHeight = this.$store.state.utils.configuration.ugc.minPlacementSize;
      // Sk8-hi quarters placement is square, if that changes we can move this to the pdm
      const minWidth = pdmModel !== 'sk8-hi' && activeUGCComponent === 'quarters' ? minPlacementSize * 2 : minPlacementSize;
      return {minWidth,minHeight}
    }
  },
  methods: {
    drawOverlay({ left, top, width, height }) {
      //    x0    x1        x2      x3
      // y0 +------------------------+
      //    |\\\\\\\\\\\\\\\\\\\\\\\\|
      //    |\\\\\\\\\\\\\\\\\\\\\\\\|
      // y1 +------+---------+-------+
      //    |\\\\\\|         |\\\\\\\|
      //    |\\\\\\|         |\\\\\\\|
      //    |\\\\\\|         |\\\\\\\|
      // y2 +------+---------+-------+
      //    |\\\\\\\\\\\\\\\\\\\\\\\\|
      //    |\\\\\\\\\\\\\\\\\\\\\\\\|
      // y3 +------------------------+
      //
      const x0 = 0;
      const x1 = left;
      const x2 = left + width;
      const x3 = this.fabricCanvas.getWidth();
      const y0 = 0;
      const y1 = top;
      const y2 = top + height;
      const y3 = this.fabricCanvas.getHeight();
      const points = [
        { x: x0, y: y0 },
        { x: x3, y: y0 },
        { x: x3, y: y3 },
        { x: x0, y: y3 },
        { x: x0, y: y2 },
        { x: x2, y: y2 },
        { x: x2, y: y1 },
        { x: x1, y: y1 },
        { x: x1, y: y2 },
        { x: x0, y: y2 },
        { x: x0, y: y0 }
      ];

      const options = {
        selectable: false,
        objectCaching: false,
        hasControls: false,
        fill: "rgba(0,0,0,0.6)"
      };
      if (this.polygon) {
        this.fabricCanvas.remove(this.polygon);
      }
      this.polygon = new fabric.Polygon(points, options);
      this.fabricCanvas.add(this.polygon);
    },
    /**
     * This function returns an object containing image data
     * @param {Fabric.Image} image
     * @return {{src: (*|HTMLCanvasElement|HTMLImageElement), width: *, height: *, top: *, left: *, scale: (*|{scaleX, scaleY})}}
     * @public
     */
    getImageMetadata(image) {
      return {
        src: image.getElement(),
        width: image.width,
        height: image.height,
        top: image.top,
        left: image.left,
        scale: image.getObjectScaling()
      };
    },
    /**
     * This function applies location/size data to an image
     * @param image
     * @param data
     * @public
     */
    loadImageMetadata({ image, data }) {
      const {
        src,
        width,
        height,
        top,
        left,
        scale: { scaleX, scaleY }
      } = data;
      image.setElement(src, { width, height, top, left });
      image.width = width;
      image.height = height;
      image.top = top;
      image.left = left;
      image.scaleX = scaleX;
      image.scaleY = scaleY;
      image.setCoords();
      this.fabricCanvas.renderAll.bind(this.fabricCanvas);
    },
    /**
     * This function shows the cropbox into the Fabric canvas
     * @public
     */
    show() {
      if (!this.fabricCanvas || this.isOpen) {
        return;
      }

      if (this.beforeShow) {
        this.beforeShow();
      }
      const canvasImage = this.fabricCanvas.getObjects("image");
      if (!canvasImage.length) {
        throw new Error("The canvas doesn't have images to crop");
      }
      this.currentImage = this.getImageMetadata(canvasImage[0]);
      this.setSelectableComponents(false);
      this.toggleOverlay(false);
      this.cropBox = new fabric.Rect({
        borderColor: "#979797",
        borderScaleFactor: 2,
        cornerColor: "white",
        cornerStrokeColor: "#979797",
        excludeFromExport: true,
        fill: "transparent",
        hasRotatingPoint: false,
        height: 1,
        lockScalingFlip: false,
        lockUniScaling: this.lockSquare,
        lockRotation: true,
        opacity: 1,
        originX: "left",
        originY: "top",
        transparentCorners: false,
        width: 1
      });

      // Setting cropBox position on canvas
      if (this.tempCropData && 
          this.tempCropData.cropBox.width < this.currentImage.width && 
          this.tempCropData.cropBox.height < this.currentImage.height)
      {
        const { cropBox: _cropBox, image: data } = this.tempCropData;
        this.cropBox.height = _cropBox.height;
        this.cropBox.left = _cropBox.left;
        this.cropBox.top = _cropBox.top;
        this.cropBox.width = _cropBox.width;
        this.loadImageMetadata({ image: canvasImage[0], data });
      } else {
        const canvasHeight = this.fabricCanvas.getHeight();
        const canvasWidth = this.fabricCanvas.getWidth();
        let rowSize = (canvasHeight <= canvasWidth ? canvasHeight : canvasWidth) / 8; // Gets row size from smaller one

        const actualCropWidth = rowSize*2*this.cropBox.scaleX/canvasImage[0].scaleX;
        const actualCropHeight = rowSize*2*this.cropBox.scaleY/canvasImage[0].scaleY;

        if(actualCropWidth < this.minPlacementSize.minWidth) {
          rowSize = this.minPlacementSize.minWidth / this.cropBox.scaleX * canvasImage[0].scaleX/2;
        } else if (actualCropHeight < this.minPlacementSize.minHeight) {
          rowSize = this.minPlacementSize.minHeight / this.cropBox.scaleY * canvasImage[0].scaleY/2;
        }

        this.cropBox.height = rowSize * 2;
        this.cropBox.width = rowSize * 2;
        this.cropBox.top = (canvasHeight / 2) - rowSize;
        this.cropBox.left = (canvasWidth / 2) - rowSize;
      }
      this.drawOverlay(this.cropBox);
      this.fabricCanvas.add(this.cropBox);

      this.cropBox.on("moving", this._onCropBoxMoved);
      this.cropBox.on("scaling", this._onCropBoxScaling);
      this.fabricCanvas.on("selection:cleared", this.setCropboxActive);

      this.setCropboxActive();
    },
    setCropboxActive() {
      this.fabricCanvas.setActiveObject(this.cropBox);
    },
    /**
     * This function hides the cropbox into the Fabric canvas
     * @public
     */
    hide() {
      // Removing cropbox listeners
      this.cropBox.off("moving", this._onCropBoxMoved);
      this.cropBox.off("scaling", this._onCropBoxScaling);
      this.fabricCanvas.off("selection:cleared", this.setCropboxActive);
      this.fabricCanvas.remove(this.cropBox);
      if (this.polygon) {
        this.fabricCanvas.remove(this.polygon);
      }

      // Reset values
      this.cropBox = null;
      this.startX = null;
      this.startY = null;

      // Unlock canvas elements
      this.setSelectableComponents(true);
      this.toggleOverlay(true);
      this.currentImage = null;
    },
    /**
     * This function returns the masked area with the cropbox
     * @return {*}
     */
    crop() {
      if (!this.isOpen || !this.fabricCanvas) {
        throw new Error("Unable to crop the image");
      }
      const canvasImage = this.fabricCanvas.getObjects("image");
      if (!canvasImage.length) {
        throw new Error("The canvas doesn't have images to crop");
      }
      this.cropBox.setCoords();
      this.cropBox.visible = false;
      const croppedArea = this.fabricCanvas.toDataURL({
        format: this.imageFormat,
        left: this.cropBox.left,
        top: this.cropBox.top,
        width: this.cropBox.getScaledWidth(),
        height: this.cropBox.getScaledHeight(),
        enableRetinaScaling: false,
        multiplier: 4
      });
      this.cropBox.visible = true;
      return croppedArea;
    },
    /**
     * Handler for the cropbox moving event. It restricts the area where the cropbox can be dragged.
     */
    _onCropBoxMoved(evt) {
      const obj = evt.target;
      const cornerLimit = obj.cornerSize / 2;
      const maxHeight = obj.canvas.height - obj.cornerSize;
      const maxWidth = obj.canvas.width - obj.cornerSize;
      // if object is bigger than canvas
      const isTooHigh = obj.currentHeight > maxHeight;
      const isTooWidth = obj.currentWidth > maxWidth;
      if (isTooHigh || isTooWidth) {
        if (isTooHigh) {
          obj.currentHeight = maxHeight;
          obj.top = cornerLimit;
        }
        if (isTooWidth) {
          obj.currentWidth = maxWidth;
          obj.left = cornerLimit;
        }
        return;
      }
      obj.setCoords();
      // top-left  corner
      if (
        obj.getBoundingRect().top - cornerLimit < 0 ||
        obj.getBoundingRect().left - cornerLimit < 0
      ) {
        obj.top = Math.max(
          obj.top,
          obj.top - obj.getBoundingRect().top + cornerLimit
        );
        obj.left = Math.max(
          obj.left,
          obj.left - obj.getBoundingRect().left + cornerLimit
        );
      }
      // bot-right corner
      if (
        obj.getBoundingRect().top + obj.getBoundingRect().height + cornerLimit >
          obj.canvas.height ||
        obj.getBoundingRect().left + obj.getBoundingRect().width + cornerLimit >
          obj.canvas.width
      ) {
        obj.top = Math.min(
          obj.top,
          obj.canvas.height -
            obj.getBoundingRect().height +
            obj.top -
            obj.getBoundingRect().top -
            cornerLimit
        );
        obj.left = Math.min(
          obj.left,
          obj.canvas.width -
            obj.getBoundingRect().width +
            obj.left -
            obj.getBoundingRect().left -
            cornerLimit
        );
      }
      const overlayData = {
        left: obj.left,
        top: obj.top,
        width: obj.getScaledWidth(),
        height: obj.getScaledHeight()
      };
      this.drawOverlay(overlayData);
    },
    /**
     * Handler for the cropbox scaling event. It restricts the area where the cropbox can be dragged.
     */
    _onCropBoxScaling(event) {
      if (!this.isOpen || !this.fabricCanvas) {
        return;
      }
      const canvasImage = this.fabricCanvas.getObjects("image");
      if(canvasImage.width < this.minPlacementSize.minWidth || canvasImage.height < this.minPlacementSize.minHeight ) 
      {
        return
      }

      const currentObject = event.target;
      if (currentObject !== this.cropBox) return;

      const canvas = this.fabricCanvas;
      const minX = currentObject.left;
      const minY = currentObject.top;
      const maxX = currentObject.left + currentObject.getScaledWidth();
      const maxY = currentObject.top + currentObject.getScaledHeight();

      if (minX < 0 || maxX > canvas.getWidth()) {
        const lastScaleX = this.lastScaleX || 1;
        currentObject.scaleX = lastScaleX;
      }
      if (minX < 0) {
        currentObject.left = 0;
      }

      if (minY < 0 || maxY > canvas.getHeight()) {
        const lastScaleY = this.lastScaleY || 1;
        currentObject.scaleY = lastScaleY;
      }
      if (minY < 0) {
        currentObject.top = 0;
      }

      if (currentObject.width < this.minWidth) {
        currentObject.scaleToWidth(this.minWidth);
      }
      if (currentObject.height < this.minHeight) {
        currentObject.scaleToHeight(this.minHeight);
      }

      // Check actual size if its smaller than the minimum allow placement size.
      const actualCropWidth = currentObject.width*currentObject.scaleX/canvasImage[0].scaleX;
      const actualCropHeight = currentObject.height*currentObject.scaleY/canvasImage[0].scaleY;
      if(actualCropWidth < this.minPlacementSize.minWidth) {
        currentObject.width = this.minPlacementSize.minWidth / currentObject.scaleX * canvasImage[0].scaleX;
      } 
      
      if (actualCropHeight < this.minPlacementSize.minHeight) {
        currentObject.height = this.minPlacementSize.minHeight / currentObject.scaleY * canvasImage[0].scaleY;
      } 

      setTimeout(() => {
        const overlayData = {
          left: currentObject.left,
          top: currentObject.top,
          width: currentObject.getScaledWidth(),
          height: currentObject.getScaledHeight()
        };

        this.lastScaleX = currentObject.scaleX;
        this.lastScaleY = currentObject.scaleY;
        this.drawOverlay(overlayData);
      },100)
    },
    // Init crop zone
    onMouseDown: function(event) {
      if (!this.isOpen || !this.fabricCanvas) {
        return;
      }

      const canvas = this.fabricCanvas;

      // recalculate offset, in case canvas was manipulated since last `calcOffset`
      canvas.calcOffset();
      const pointer = canvas.getPointer(event.e);
      const x = pointer.x;
      const y = pointer.y;
      const point = new fabric.Point(x, y);

      // Check if user want to scale or drag the crop zone.
      const activeObject = canvas.getObjects()[0];
      if (activeObject === this.cropBox || this.cropBox.containsPoint(point)) {
        return;
      }

      canvas.discardActiveObject();
      this.cropBox.width = 0;
      this.cropBox.height = 0;
      this.cropBox.scaleX = 1;
      this.cropBox.scaleY = 1;

      this.startX = x;
      this.startY = y;
    },

    // Extend crop zone
    onMouseMove: function(event) {
      if (null === this.startX || null === this.startY) {
        return;
      }

      const canvas = this.fabricCanvas;
      const pointer = canvas.getPointer(event.e);
      const x = pointer.x;
      const y = pointer.y;

      this._renderCropZone(this.startX, this.startY, x, y);
    },

    // Finish crop zone
    onMouseUp: function() {
      if (null === this.startX || null === this.startY) {
        return;
      }

      const canvas = this.fabricCanvas;
      this.cropBox.setCoords();
      canvas.setActiveObject(this.cropBox);
      canvas.calcOffset();

      this.startX = null;
      this.startY = null;
    },

    _renderCropZone(fromX, fromY, toX, toY) {
      const canvas = this.fabricCanvas;
      const ctx = canvas.getContext();
      ctx.rect(20, 20, 150, 100);
      ctx.stroke();

      const isRight = toX > fromX;
      const isDown = toY > fromY;

      const minWidth = Math.min(+this.minWidth, canvas.getWidth());
      const minHeight = Math.min(+this.minHeight, canvas.getHeight());

      // Define corner coordinates
      let leftX = Math.min(fromX, toX);
      let rightX = Math.max(fromX, toX);
      let topY = Math.min(fromY, toY);
      let bottomY = Math.max(fromY, toY);

      // Replace current point into the canvas
      leftX = Math.max(0, leftX);
      rightX = Math.min(canvas.getWidth(), rightX);
      topY = Math.max(0, topY);
      bottomY = Math.min(canvas.getHeight(), bottomY);

      // Recalibrate coordinates according to given options
      if (rightX - leftX < minWidth) {
        if (isRight) rightX = leftX + minWidth;
        else leftX = rightX - minWidth;
      }
      if (bottomY - topY < minHeight) {
        if (isDown) bottomY = topY + minHeight;
        else topY = bottomY - minHeight;
      }

      // Truncate truncate according to canvas dimensions
      if (leftX < 0) {
        // Translate to the left
        rightX += Math.abs(leftX);
        leftX = 0;
      }
      if (rightX > canvas.getWidth()) {
        // Translate to the right
        leftX -= rightX - canvas.getWidth();
        rightX = canvas.getWidth();
      }
      if (topY < 0) {
        // Translate to the bottom
        bottomY += Math.abs(topY);
        topY = 0;
      }
      if (bottomY > canvas.getHeight()) {
        // Translate to the right
        topY -= bottomY - canvas.getHeight();
        bottomY = canvas.getHeight();
      }

      const width = rightX - leftX;
      const height = bottomY - topY;

      // Apply coordinates
      this.cropBox.left = leftX;
      this.cropBox.top = topY;
      this.cropBox.width = width;
      this.cropBox.height = height;

      this.fabricCanvas.bringToFront(this.cropBox);
    },

    // Lock/Unlock  visible canvas components
    /**
     * This function prevent other objects (besides the cropbox) from being selected
     * @public
     */
    setSelectableComponents(unlocked) {
      let totalObjects = this.fabricCanvas.getObjects().length;
      while (totalObjects--) {
        const obj = this.fabricCanvas.item(totalObjects);
        if (obj.visible) {
          obj.selectable = unlocked;
          //obj.evented = unlocked;
          //obj.hasControls = unlocked;
        }
      }
    },
    /**
     * This function hide/show the overlay image from the Fabric canvas
     * @param display
     */
    toggleOverlay(display) {
      if (display) {
        this.fabricCanvas.overlayImage = this.overlayImage;
        this.overlayImage = null;
      } else {
        this.overlayImage = this.fabricCanvas.overlayImage;
        this.fabricCanvas.overlayImage = null;
      }
      this.fabricCanvas.renderAll.bind(this.fabricCanvas);
    }
  },
  mounted() {
    this.show();
  },
  /**
   * This handle the use case where the user clicks the crop icon and suddenly clicks another tool icon
   */
  beforeDestroy() {
    if (this.cropBox) {
      try {
        this.cancel();
      } catch (e) {
        console.warn("An exception occurred when hiding the cropper");
      }
    }
  },
  render() {
    return null;
  }
};
</script>
